import React, { useState, useEffect } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Star, Quote, Send, ChevronLeft, ChevronRight } from 'lucide-react';
import { testimonials } from '../data/mock';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Testimonials = () => {
  const { toast } = useToast();
  const [testimonialForm, setTestimonialForm] = useState({
    name: '',
    text: '',
    rating: 5
  });
  const [publishedTestimonials, setPublishedTestimonials] = useState([]);
  const [currentTestimonialIndex, setCurrentTestimonialIndex] = useState(0);

  const fetchPublishedTestimonials = async () => {
    try {
      const response = await axios.get(`${API}/testimonials/published`);
      setPublishedTestimonials(response.data);
    } catch (error) {
      console.error('Error fetching published testimonials:', error);
    }
  };

  // Fetch published testimonials on component mount
  useEffect(() => {
    fetchPublishedTestimonials();
  }, []);

  const nextTestimonial = () => {
    const allTestimonials = [...testimonials, ...publishedTestimonials];
    setCurrentTestimonialIndex((prev) => 
      prev === allTestimonials.length - 1 ? 0 : prev + 1
    );
  };

  const prevTestimonial = () => {
    const allTestimonials = [...testimonials, ...publishedTestimonials];
    setCurrentTestimonialIndex((prev) => 
      prev === 0 ? allTestimonials.length - 1 : prev - 1
    );
  };

  const handleSubmitTestimonial = async (e) => {
    e.preventDefault();
    
    if (!testimonialForm.name.trim() || !testimonialForm.text.trim()) {
      toast({
        title: "Fehler",
        description: "Bitte füllen Sie alle Felder aus.",
        variant: "destructive"
      });
      return;
    }

    try {
      const response = await axios.post(`${API}/testimonials`, {
        name: testimonialForm.name.trim(),
        text: testimonialForm.text.trim(),
        rating: testimonialForm.rating
      });

      toast({
        title: "Vielen Dank!",
        description: "Ihre Bewertung wurde erfolgreich übermittelt und wird nach Prüfung veröffentlicht.",
      });

      setTestimonialForm({ name: '', text: '', rating: 5 });
    } catch (error) {
      console.error('Testimonial submission error:', error);
      toast({
        title: "Fehler beim Senden",
        description: "Entschuldigung, es gab ein Problem beim Senden Ihrer Bewertung. Bitte versuchen Sie es später erneut.",
        variant: "destructive"
      });
    }
  };

  const renderStars = (rating, interactive = false, onRatingChange = null) => {
    return (
      <div className="flex">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`h-5 w-5 ${
              star <= rating 
                ? 'text-yellow-400 fill-current' 
                : 'text-gray-300'
            } ${interactive ? 'cursor-pointer hover:text-yellow-400' : ''}`}
            onClick={interactive ? () => onRatingChange(star) : undefined}
          />
        ))}
      </div>
    );
  };

  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Patientenstimmen
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-4">
            Das Vertrauen unserer Patientinnen ist unser wertvollstes Gut. 
            Hier erfahren Sie, was andere über unsere Praxis sagen.
          </p>
          <div className="flex items-center justify-center">
            <div className="flex items-center bg-blue-50 px-4 py-2 rounded-lg">
              <Star className="h-5 w-5 text-yellow-400 fill-current mr-2" />
              <span className="text-blue-700 font-medium text-sm">
                Echte Google Bewertungen von unseren Patientinnen
              </span>
            </div>
          </div>
        </div>

        {/* Google Reviews + Published User Testimonials Carousel */}
        <div className="mb-16">
          {(() => {
            const allTestimonials = [...testimonials, ...publishedTestimonials];
            if (allTestimonials.length === 0) return null;
            
            return (
              <div className="relative">
                <div className="flex items-center justify-center mb-8">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={prevTestimonial}
                    className="mr-4 border-red-300 text-red-700 hover:bg-red-50"
                    disabled={allTestimonials.length <= 1}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <span className="text-sm text-gray-500">
                    {currentTestimonialIndex + 1} von {allTestimonials.length} Bewertungen
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={nextTestimonial}
                    className="ml-4 border-red-300 text-red-700 hover:bg-red-50"
                    disabled={allTestimonials.length <= 1}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
                
                <div className="max-w-2xl mx-auto">
                  <Card className="bg-gradient-to-br from-red-50 to-red-100 border-red-200 shadow-lg">
                    <CardContent className="p-8">
                      <div className="flex items-center justify-between mb-6">
                        <div className="flex items-center">
                          <Quote className="h-8 w-8 text-red-400 mr-3" />
                          {allTestimonials[currentTestimonialIndex]?.isGoogleReview && (
                            <span className="bg-blue-100 text-blue-700 text-xs px-3 py-1 rounded-full font-medium">
                              Google Bewertung
                            </span>
                          )}
                          {!allTestimonials[currentTestimonialIndex]?.isGoogleReview && publishedTestimonials.includes(allTestimonials[currentTestimonialIndex]) && (
                            <span className="bg-green-100 text-green-700 text-xs px-3 py-1 rounded-full font-medium">
                              Patientenbewertung
                            </span>
                          )}
                        </div>
                        {renderStars(allTestimonials[currentTestimonialIndex]?.rating || 5)}
                      </div>
                      <p className="text-gray-700 mb-6 italic leading-relaxed text-lg text-center">
                        "{allTestimonials[currentTestimonialIndex]?.text}"
                      </p>
                      <div className="flex justify-between items-center text-sm">
                        <span className="font-medium text-gray-900">
                          {allTestimonials[currentTestimonialIndex]?.name}
                        </span>
                        <span className="text-gray-500">
                          {allTestimonials[currentTestimonialIndex]?.date || 
                           new Date(allTestimonials[currentTestimonialIndex]?.created_at).toLocaleDateString('de-DE')}
                        </span>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </div>
            );
          })()}
        </div>
      </div>
    </section>
  );
};

export default Testimonials;